import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';
import React from 'react';

import {
  BodyCell,
  BodyCheckboxCell,
  EmptyBox,
  HeaderCell,
  HeaderCheckboxCell,
  LoadingBox,
  styledTableContainerSx,
} from './DataTable.styles';

export interface DataTableColumn<T> {
  id: string;
  label: React.ReactNode;
  render: (row: T) => React.ReactNode;
  width?: string | number;
  align?: 'left' | 'right' | 'center';
  sortable?: boolean;
  sortValue?: (row: T) => number | string;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  getRowId: (row: T) => string;
  selectedIds?: string[];
  onSelectionChange?: (ids: string[]) => void;
  onRowClick?: (id: string) => void;
  loading?: boolean;
  emptyMessage?: string;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (columnId: string) => void;
  renderExpandedRow?: (row: T, colSpan: number) => React.ReactNode;
  /** Overrides the header row's default (accent-tinted) background color. */
  headerBgColor?: string;
  /** Overrides the header row's default (9px) font size. */
  headerFontSize?: string;
  /** Overrides the body row's default hover background color. */
  rowHoverColor?: string;
  /** Overrides the body row's default (52px) height. */
  rowHeight?: number;
  /** Overrides the header/body cells' default (16px) horizontal padding. */
  cellPaddingX?: number;
  /** Overrides the header row's default text color. */
  headerTextColor?: string;
  /** Overrides the outer container's default border/radius/shadow. Plain style object only. */
  containerSx?: Record<string, string | number>;
  /** Overrides the compact header row's default (44px) height. Only applies with headerFontSize. */
  headerHeight?: number;
  /** Overrides the compact header row's default (8px) vertical padding. Only applies with headerFontSize. */
  headerPaddingY?: number;
  /** Overrides the body cells' default (10px) vertical padding. */
  cellPaddingY?: number;
  /** Removes the outer Paper border — only row separators remain. */
  borderless?: boolean;
  /** Overrides the header text color. */
  headerColor?: string;
  /** Overrides the header font weight. */
  headerWeight?: number;
  /** Uses table-layout: fixed so every column's explicit `width` (px or %) is honored exactly,
   * instead of the browser redistributing space based on cell content. */
  fixedLayout?: boolean;
}

export function DataTable<T>({
  columns,
  rows,
  getRowId,
  selectedIds = [],
  onSelectionChange,
  onRowClick,
  loading = false,
  emptyMessage = 'No data available',
  sortColumn,
  sortDirection,
  onSort,
  renderExpandedRow,
  headerBgColor,
  headerFontSize,
  headerColor,
  headerWeight,
  rowHoverColor,
  rowHeight,
  cellPaddingX,
  headerTextColor,
  containerSx,
  headerHeight,
  headerPaddingY,
  cellPaddingY,
  borderless = false,
  fixedLayout = false,
}: DataTableProps<T>) {
  const theme = useTheme();
  const selectable = Boolean(onSelectionChange);
  const colSpan = columns.length + (selectable ? 1 : 0);

  const allSelected = rows.length > 0 && rows.every((row) => selectedIds.includes(getRowId(row)));
  const someSelected = rows.some((row) => selectedIds.includes(getRowId(row))) && !allSelected;

  const handleSelectAll = () => {
    if (!onSelectionChange) return;
    onSelectionChange(allSelected ? [] : rows.map(getRowId));
  };

  const handleSelectRow = (id: string) => {
    if (!onSelectionChange) return;
    onSelectionChange(
      selectedIds.includes(id) ? selectedIds.filter((s) => s !== id) : [...selectedIds, id],
    );
  };

  const handleSort = (columnId: string) => {
    if (!onSort) return;
    onSort(columnId);
  };

  return (
    <TableContainer
      component={Paper}
      variant={borderless ? 'elevation' : 'outlined'}
      elevation={0}
      sx={{ ...styledTableContainerSx(theme, borderless), ...containerSx }}
    >
      <Table stickyHeader sx={fixedLayout ? { tableLayout: 'fixed' } : undefined}>
        <TableHead>
          <TableRow>
            {selectable && (
              <HeaderCheckboxCell bgColor={headerBgColor}>
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={handleSelectAll}
                  size="small"
                />
              </HeaderCheckboxCell>
            )}
            {columns.map((col) => (
              <HeaderCell
                key={col.id}
                align={col.align ?? 'left'}
                sx={col.width !== undefined ? { width: col.width } : undefined}
                sortable={col.sortable}
                sortDirection={col.sortable && sortColumn === col.id ? sortDirection : false}
                bgColor={headerBgColor}
                fontSize={headerFontSize}
                paddingX={cellPaddingX}
                textColor={headerTextColor}
                headerHeight={headerHeight}
                paddingY={headerPaddingY}
                headerColor={headerColor}
                headerWeight={headerWeight}
              >
                {col.sortable ? (
                  <TableSortLabel
                    active={sortColumn === col.id}
                    direction={sortColumn === col.id ? sortDirection : 'asc'}
                    onClick={() => handleSort(col.id)}
                  >
                    {col.label}
                    {sortColumn === col.id ? (
                      <Box component="span" sx={visuallyHidden}>
                        {sortDirection === 'desc' ? 'sorted descending' : 'sorted ascending'}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                ) : (
                  col.label
                )}
              </HeaderCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={colSpan} sx={{ border: 0 }}>
                <LoadingBox>
                  <CircularProgress size={32} />
                </LoadingBox>
              </TableCell>
            </TableRow>
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={colSpan} sx={{ border: 0 }}>
                <EmptyBox>
                  <Typography variant="body2" color="text.secondary">
                    {emptyMessage}
                  </Typography>
                </EmptyBox>
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row, index) => {
              const rowId = getRowId(row);
              const isSelected = selectedIds.includes(rowId);
              return (
                <React.Fragment key={rowId}>
                  <TableRow
                    selected={isSelected}
                    hover
                    onClick={onRowClick ? () => onRowClick(rowId) : undefined}
                    sx={{
                      ...(onRowClick ? { cursor: 'pointer' } : null),
                      ...(rowHoverColor
                        ? { '&:hover': { backgroundColor: `${rowHoverColor} !important` } }
                        : null),
                    }}
                  >
                    {selectable && (
                      <BodyCheckboxCell>
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleSelectRow(rowId)}
                          size="small"
                        />
                      </BodyCheckboxCell>
                    )}
                    {columns.map((col) => (
                      <BodyCell
                        key={col.id}
                        align={col.align ?? 'left'}
                        rowHeight={rowHeight}
                        paddingX={cellPaddingX}
                        paddingY={cellPaddingY}
                        compact={Boolean(headerFontSize)}
                        isFirstRow={index === 0}
                      >
                        {col.render(row)}
                      </BodyCell>
                    ))}
                  </TableRow>
                  {renderExpandedRow && renderExpandedRow(row, colSpan)}
                </React.Fragment>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
